import { useParams, Link } from "react-router-dom";
import {
 Container, Section, Breadcrumbs, ScheduleBtn, CallBtn, MapEmbed, Loader, BtnPrimary,
 CheckCircle2, ArrowRight,
} from "../components/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useLocation as useLoc, useServices } from "../lib/api";
import { useSeo, breadcrumbLd, faqLd } from "../lib/seo";

export default function LocationDetail() {
 const { slug } = useParams();
 const { data: loc, isLoading, isError } = useLoc(slug);
 const { data: services = [] } = useServices();

 useSeo({
 title: loc?.metaTitle,
 description: loc?.metaDescription,
 path: `/service-areas/${slug}`,
 image: loc?.image,
 jsonLd: loc ? [
 { "@context": "https://schema.org", "@type": "AutoRepair", name: "RPM Auto Repair",
 telephone: "+19516534777", areaServed: { "@type": "City", name: loc.name },
 address: { "@type": "PostalAddress", streetAddress: "2650 E Alessandro Blvd", addressLocality: "Riverside", addressRegion: "CA", postalCode: "92508", addressCountry: "US" } },
 faqLd(loc.faqs),
 breadcrumbLd([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: loc.name, path: `/service-areas/${slug}` }]),
 ] : [],
 });

 if (isLoading) return <Loader />;
 if (isError || !loc) return <Container className="py-32 text-center"><h1 className="font-display text-3xl text-white">Area Not Found</h1><div className="mt-6 flex justify-center"><BtnPrimary to="/service-areas">All Service Areas</BtnPrimary></div></Container>;

 return (
 <>
 <section className="relative overflow-hidden border-b border-white/10">
 <img src={loc.image} alt={`Auto repair serving ${loc.name}, CA`} className="absolute inset-0 h-full w-full object-cover opacity-25" />
 <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
 <Container className="relative py-16 sm:py-20">
 <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }, { name: loc.name, path: `/service-areas/${slug}` }]} />
 <h1 className="max-w-4xl font-display text-4xl font-700 leading-[1.05] text-white sm:text-5xl lg:text-6xl">Auto Repair in {loc.name}, CA</h1>
 <p className="mt-5 max-w-2xl text-lg text-white/75">Trusted, ASE Master Certified auto repair for {loc.name} drivers, {loc.distance !== "0 mi" ? `just ${loc.distance} from your neighborhood` : "right here at home"}.</p>
 <div className="mt-8 flex flex-col gap-4 sm:flex-row"><ScheduleBtn label="Schedule Appointment" /><CallBtn /></div>
 </Container>
 </section>

 <Section>
 <Container className="grid gap-12 lg:grid-cols-[1fr_360px]">
 <div>
 <article className="prose-rpm max-w-none" dangerouslySetInnerHTML={{ __html: loc.body }} />

 <h2 className="mt-12 mb-6 font-display text-3xl font-600 text-white">{loc.name} FAQs</h2>
 <Accordion type="single" collapsible className="space-y-3">
 {loc.faqs.map((f, i) => (
 <AccordionItem key={i} value={`f${i}`} className="rounded-xl border border-white/10 bg-steel px-5">
 <AccordionTrigger className="text-left font-display text-base text-white hover:no-underline">{f.q}</AccordionTrigger>
 <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
 </AccordionItem>
 ))}
 </Accordion>
 </div>

 <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
 <MapEmbed query={`${loc.name}, CA`} title={`Map serving ${loc.name}`} />
 <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-[#1a1013] to-steel p-6">
 <h3 className="font-display text-xl text-white">Book from {loc.name}</h3>
 <p className="mt-2 text-sm text-muted-foreground">Fast, honest service is a short drive away. Reserve your spot today.</p>
 <div className="mt-5 space-y-3"><ScheduleBtn className="w-full" /><CallBtn className="w-full" /></div>
 </div>
 <div className="rounded-xl border border-white/10 bg-steel p-6">
 <h3 className="font-display text-lg text-white">Popular Services</h3>
 <ul className="mt-4 space-y-2">
 {services.slice(0, 6).map((s) => (
 <li key={s.slug}><Link to={`/services/${s.slug}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"><CheckCircle2 size={14} className="text-primary" /> {s.name}</Link></li>
 ))}
 <li><Link to="/services" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary">All Services <ArrowRight size={13} /></Link></li>
 </ul>
 </div>
 </aside>
 </Container>
 </Section>
 </>
 );
}
